import React from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from '@tanstack/react-store';
import {
  columnAlignClass,
  flexRender,
  Header,
  ReactTable,
  Row,
  type RowData,
} from '@/lib/tanstack-table';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVerticalIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type TableDensity,
} from '@/components/ui/table';
import { PERSISTENT_SCROLLBAR } from '@/lib/persistentScrollbar';
import { cn } from '@/lib/utils';
import {
  DataTableBodyRow,
  DataTableGroupRow,
  type RowMeasure,
} from './data-table-body-row';
import {
  resolveColumnSizing,
  useLockedColumnWidths,
  useVirtualRows,
  type DataTableVirtualization,
} from './use-virtual-rows';

/**
 * True once the user (or their persisted layout) has set explicit column
 * widths — the signal that flips a resizable table from content-driven auto
 * layout to fixed layout. See the sizing-model comment in DataTable.
 */
function hasCustomColumnSizing(columnSizing: Record<string, number>): boolean {
  return Object.keys(columnSizing).length > 0;
}

interface DataTableProps<TData extends RowData> {
  table: ReactTable<TData>;
  /**
   * Called when a row is clicked. A click anywhere in the `select` column
   * toggles that row instead and does not call this — the checkbox is a
   * small target, and a near-miss should not open the row. The event is
   * passed through for every other cell (`data-column-id` on each `<td>`).
   */
  onRowClick?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowMouseDown?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowContextMenu?: (e: React.MouseEvent, row: TData) => void;
  /**
   * Hover/focus signal for rows that navigate. Rows using `onRowClick` navigate
   * programmatically, so TanStack Router's `defaultPreload: 'intent'` never
   * fires for them — this is where a caller preloads the destination route.
   */
  onRowIntent?: (row: TData) => void;
  className?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  groupHeaderFn?: (row: Row<TData>, index: number) => React.ReactNode | null;
  /**
   * Hides a row's body line while still rendering its `groupHeaderFn` header,
   * e.g. for collapsible groups.
   */
  isRowHidden?: (row: Row<TData>) => boolean;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /** Sets `aria-selected` on a row, for tables whose rows open a detail pane. */
  getRowAriaSelected?: (row: Row<TData>) => boolean;
  /** DOM `id` for a row, e.g. so an `aria-activedescendant` can point at it. */
  getRowDomId?: (row: Row<TData>) => string | undefined;
  /** Extra attributes for the `<table>` (role, tabIndex, aria-*). */
  tableProps?: React.ComponentProps<'table'>;
  /**
   * Opt-in: render each row as a real link, so right-click → "Open in new
   * tab", cmd-click and middle-click all work natively while a plain click
   * still navigates in place. Return a TanStack Router `<Link>` and spread
   * the supplied `className`, which stretches it over the whole row.
   *
   * The link overlays the cell content; a cell with its own clickable control
   * must give that control `relative z-10` to rise above it. When set, the
   * link owns navigation — don't also pass `onRowClick` to navigate.
   * Mirrors `GentlyTable`'s prop of the same name.
   */
  renderRowLink?: (row: TData, className: string) => React.ReactNode;
  /**
   * Adds a drag grip to each header for column reordering. The new order is
   * written through the table's `onColumnOrderChange`, so pair this with
   * `columnOrder` state on the owner (e.g. `useTableColumnSettings`).
   * Utility columns that declare `enableHiding: false` (selection checkboxes,
   * row actions) are treated as pinned and get no grip.
   *
   * Column *resizing* needs no prop here: set `enableColumnResizing: true`
   * (+ `columnResizeMode: 'onChange'`) on the table and the resize handles
   * render automatically.
   */
  enableColumnReorder?: boolean;
  /** Row/type density; `compact` gives 28px rows for dense operator grids. */
  density?: TableDensity;
  /**
   * Draw only the rows in view of the scrolling ancestor (a page section or
   * panel), for tables of hundreds of rows or more. Rows are measured once
   * drawn, so they may vary in height.
   */
  virtualize?: DataTableVirtualization;
}

/** A body line: a group header row, or a data row. */
interface BodyItem<TData extends RowData> {
  key: string;
  row: Row<TData>;
  header: React.ReactNode | null;
}

/** One entry per drawn `<tr>`: each row's group header, then the row unless hidden. */
function bodyItems<TData extends RowData>(
  rows: Row<TData>[],
  groupHeaderFn: DataTableProps<TData>['groupHeaderFn'],
  isRowHidden: DataTableProps<TData>['isRowHidden'],
): BodyItem<TData>[] {
  const items: BodyItem<TData>[] = [];
  rows.forEach((row, index) => {
    const header = groupHeaderFn?.(row, index) ?? null;
    if (header !== null) items.push({ key: `group:${row.id}`, row, header });
    if (!isRowHidden?.(row)) items.push({ key: row.id, row, header: null });
  });
  return items;
}

/** Stands in for the rows a virtualized body leaves undrawn. */
function SpacerRow({
  height,
  colSpan,
  ref,
}: {
  height: number;
  colSpan: number;
  ref?: React.Ref<HTMLTableRowElement>;
}) {
  return (
    <tr ref={ref} aria-hidden style={{ height }}>
      <td colSpan={colSpan} className="p-0" />
    </tr>
  );
}

export function DataTable<TData extends RowData>({
  table,
  onRowClick,
  onRowMouseDown,
  onRowContextMenu,
  onRowIntent,
  className,
  isLoading = false,
  emptyMessage = 'No results.',
  emptyState,
  groupHeaderFn,
  isRowHidden,
  getRowClassName,
  getRowAriaSelected,
  getRowDomId,
  tableProps,
  renderRowLink,
  enableColumnReorder = false,
  density,
  virtualize,
}: DataTableProps<TData>) {
  const tableState = useSelector(table.store, (state) => state);
  const columns = table.getAllColumns();
  const rowModel = table.getRowModel();
  const totalSize = table.getTotalSize();
  const items = bodyItems(rowModel.rows, groupHeaderFn, isRowHidden);
  const {
    rows: virtualRows,
    setStart,
    wrapper,
    wrapperRef,
  } = useVirtualRows({
    options: virtualize,
    count: items.length,
    getItemKey: (index) => items[index]?.key ?? String(index),
  });
  const windowRows = virtualRows.mode === 'window' ? virtualRows : null;
  const lockedSizing = useLockedColumnWidths({
    enabled: windowRows !== null,
    hasRows: (windowRows?.items.length ?? 0) > 0,
    // Re-measure when the row set (filter, sort) or the shown columns change,
    // not on every edit.
    layoutKey: `${items.length}:${items[0]?.key}:${items.at(-1)?.key}:${table
      .getVisibleLeafColumns()
      .map((column) => column.id)
      .join(',')}`,
    wrapper,
  });

  // Distance activation so plain clicks on the grip never start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Keyboard reordering: focus a grip, press Space/Enter to pick the column
    // up, arrows to move it, Space/Enter to drop.
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Pinned utility columns (enableHiding: false) are excluded, and the set is
  // derived from the visible leaf columns so it always matches the rendered
  // headers.
  const draggableHeaderIds: string[] = [];
  for (const column of table.getVisibleLeafColumns()) {
    if (column.getCanHide()) draggableHeaderIds.push(column.id);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // An empty columnOrder means "definition order" — materialize it so the
    // first drag has real indices to move.
    const state = tableState.columnOrder ?? [];
    const current = state.length
      ? [...state]
      : table.getAllLeafColumns().map((column) => column.id);
    // Reorder only the draggable slots and splice them back into place:
    // arrayMove over the full order would shift a pinned (or hidden) column
    // sitting between the drag's endpoints out of its position.
    const draggableIds = new Set(draggableHeaderIds);
    const movable = current.filter((id) => draggableIds.has(id));
    const from = movable.indexOf(String(active.id));
    const to = movable.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const reordered = arrayMove(movable, from, to);
    let slot = 0;
    table.setColumnOrder(
      current.map((id) => (draggableIds.has(id) ? reordered[slot++] : id)),
    );
  };

  // Sizing model for resizable tables, in two phases:
  //
  // 1. No custom sizing yet (nothing persisted, nothing dragged): the
  //    browser's auto table layout sizes columns to their content — the
  //    sensible default, with the column `size` hints as guidance.
  // 2. The moment a resize drag starts, ColumnResizeHandle snapshots every
  //    rendered column width into columnSizing and the table flips to a
  //    fixed layout with an explicit width (the sum of the column sizes).
  //    The flip is pixel-identical, and from then on a drag moves only the
  //    dragged edge — auto layout would redistribute the total so columns
  //    appear to grow from both sides — and a wide column set overflows into
  //    horizontal scroll (the outer div) rather than squishing, which also
  //    helps low-resolution windows. minWidth keeps a narrow column set
  //    filling the pane; the width-less filler cell absorbs that slack
  //    (fixed layout gives leftover space to columns without a width), so
  //    the sized columns hold their exact pixel widths and the drag tracks
  //    the cursor at every table width. Cells clip overflow in this phase,
  //    since content no longer influences its column's width.
  const isResizable = table.options.enableColumnResizing === true;
  const {
    sizing: columnSizing,
    fixed: useFixedLayout,
    width: fixedWidth,
  } = resolveColumnSizing({
    locked: lockedSizing,
    stateSizing: tableState.columnSizing,
    isResizable,
    totalSize,
  });

  // Loading/empty/group rows must also span the trailing filler cell that
  // fixed-layout header and data rows render.
  const fullRowColSpan = columns.length + (useFixedLayout ? 1 : 0);

  const headerRows = table.getHeaderGroups().map((headerGroup) => (
    <TableRow key={headerGroup.id}>
      {enableColumnReorder ? (
        <SortableContext
          items={draggableHeaderIds}
          strategy={horizontalListSortingStrategy}
        >
          {headerGroup.headers.map((header) =>
            header.column.getCanHide() ? (
              <DraggableTableHead
                key={header.id}
                header={header}
                columnSizing={columnSizing}
              />
            ) : (
              <StaticTableHead
                key={header.id}
                header={header}
                columnSizing={columnSizing}
              />
            ),
          )}
        </SortableContext>
      ) : (
        headerGroup.headers.map((header) => (
          <StaticTableHead
            key={header.id}
            header={header}
            columnSizing={columnSizing}
          />
        ))
      )}
      {useFixedLayout && <TableHead aria-hidden className="p-0" />}
    </TableRow>
  ));

  // `windowed` is set for virtualized bodies: the row is measured once drawn.
  const renderItem = (
    item: BodyItem<TData>,
    index?: number,
    windowed?: { measureElement: (el: Element | null) => void },
  ) => {
    const measure: RowMeasure | undefined = windowed && {
      'data-index': index,
      ref: windowed.measureElement,
    };
    if (item.header !== null) {
      return (
        <DataTableGroupRow
          key={item.key}
          measure={measure}
          density={density}
          colSpan={fullRowColSpan}
        >
          {item.header}
        </DataTableGroupRow>
      );
    }
    const { row } = item;
    return (
      <DataTableBodyRow
        key={item.key}
        row={row}
        measure={measure}
        domId={getRowDomId?.(row)}
        selected={tableState.rowSelection[row.id] === true}
        ariaSelected={getRowAriaSelected?.(row)}
        className={getRowClassName?.(row)}
        fixedLayout={useFixedLayout}
        onRowClick={onRowClick}
        onRowMouseDown={onRowMouseDown}
        onRowIntent={onRowIntent}
        onRowContextMenu={onRowContextMenu}
        renderRowLink={renderRowLink}
      />
    );
  };

  const tableContent = (
    <Table
      {...tableProps}
      density={density}
      className={cn(useFixedLayout && 'table-fixed', tableProps?.className)}
      style={
        useFixedLayout ? { width: fixedWidth, minWidth: '100%' } : undefined
      }
    >
      <TableHeader sticky>{headerRows}</TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={fullRowColSpan} className="h-24 text-center">
              Loading...
            </TableCell>
          </TableRow>
        ) : rowModel.rows.length ? (
          windowRows === null ? (
            items.map((item) => renderItem(item))
          ) : (
            windowRows.segments.map((segment) =>
              segment.kind === 'item' ? (
                renderItem(
                  items[segment.item.index],
                  segment.item.index,
                  windowRows,
                )
              ) : (
                <SpacerRow
                  key={segment.id}
                  ref={segment.start ? setStart : undefined}
                  height={segment.height}
                  colSpan={fullRowColSpan}
                />
              ),
            )
          )
        ) : (
          <TableRow>
            <TableCell colSpan={fullRowColSpan} className="h-24 text-center">
              {emptyState ?? emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'rounded-md border overflow-auto',
        PERSISTENT_SCROLLBAR,
        className,
      )}
    >
      {enableColumnReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {tableContent}
        </DndContext>
      ) : (
        tableContent
      )}
    </div>
  );
}

/**
 * Drag handle to reorder a resizable column's right edge. Rendered whenever
 * the table enables column resizing (`header.column.getCanResize()`); the
 * width streams through `columnSizing` state while dragging.
 */
function ColumnResizeHandle<TData extends RowData>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  const columnResizing = useSelector(
    header.getContext().table.atoms.columnResizing,
  );
  if (!header.column.getCanResize()) return null;

  // First-ever resize: the table is still in content-driven auto layout, so
  // snapshot every rendered header width into columnSizing before starting
  // the drag. flushSync applies the snapshot immediately so TanStack's
  // resize handler reads the on-screen width as the drag's starting point —
  // the flip to fixed layout is pixel-identical instead of snapping every
  // column to its default size.
  const beginResize = (event: React.MouseEvent | React.TouchEvent) => {
    const { table } = header.getContext();
    // The table's own sizing: a virtualized table's measured widths are on
    // screen but not in state, so they still need snapshotting.
    if (!hasCustomColumnSizing(table.store.state.columnSizing)) {
      const tableEl = (event.currentTarget as HTMLElement).closest('table');
      const snapshot: Record<string, number> = {};
      tableEl
        ?.querySelectorAll<HTMLTableCellElement>('th[data-column-id]')
        .forEach((th) => {
          const id = th.dataset.columnId;
          if (id) snapshot[id] = Math.round(th.getBoundingClientRect().width);
        });
      if (Object.keys(snapshot).length > 0) {
        flushSync(() => table.setColumnSizing(snapshot));
      }
    }
    header.getResizeHandler()(event);
  };

  // Pointer-only drag affordance: not focusable and no keyboard resize, so it
  // stays out of the accessibility tree rather than exposing an inoperable,
  // unnamed separator.
  return (
    <div
      aria-hidden="true"
      onMouseDown={beginResize}
      onTouchStart={beginResize}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none',
        // Always-visible tick marking the resize boundary, drawn as a
        // centered 1px line inside the wider (invisible) hit area; it grows
        // to a full-height primary line on hover and while dragging.
        "after:absolute after:right-[2px] after:top-1/2 after:w-px after:-translate-y-1/2 after:content-['']",
        columnResizing.isResizingColumn === header.column.id
          ? 'after:h-full after:bg-primary'
          : 'after:h-4 after:bg-border hover:after:h-full hover:after:bg-primary/60',
      )}
    />
  );
}

function StaticTableHead<TData extends RowData>({
  header,
  columnSizing,
}: {
  header: Header<TData, unknown>;
  columnSizing: Record<string, number>;
}) {
  return (
    <TableHead
      data-column-id={header.column.id}
      style={{ width: columnSizing[header.column.id] ?? header.getSize() }}
      className={cn(
        header.column.getCanResize() && 'relative',
        hasCustomColumnSizing(columnSizing) && 'overflow-hidden',
        columnAlignClass(header.column.columnDef.meta),
      )}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      <ColumnResizeHandle header={header} />
    </TableHead>
  );
}

/**
 * Header cell with a drag grip for column reordering. The grip is a separate
 * button so sorting clicks (the header title) and resizing (the right-edge
 * handle) keep working unchanged next to it.
 */
function DraggableTableHead<TData extends RowData>({
  header,
  columnSizing,
}: {
  header: Header<TData, unknown>;
  columnSizing: Record<string, number>;
}) {
  const gt = useGT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id });

  return (
    <TableHead
      ref={setNodeRef}
      data-column-id={header.column.id}
      style={{
        width: columnSizing[header.column.id] ?? header.getSize(),
        // Translate only — the sortable strategy's transform carries a scale
        // component sized against the displaced neighbor, which visibly warps
        // the dragged header when column widths differ.
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(
        'relative',
        hasCustomColumnSizing(columnSizing) && 'overflow-hidden',
        isDragging && 'z-10 opacity-60',
        columnAlignClass(header.column.columnDef.meta),
      )}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label={gt('Reorder column', {
            context: 'drag grip on a table column header',
          })}
        >
          <DotsSixVerticalIcon className="h-3.5 w-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </div>
      <ColumnResizeHandle header={header} />
    </TableHead>
  );
}
